/**
 * The test-pose gallery plus the CPU skinning the leak checker runs on.
 *
 * Canonical space (plan D4): Y up, the character faces +Z, character-left is +X, arms out along
 * ±X in a T-pose and **every bone has identity local rotation in rest**. A canonical pose
 * quaternion is a rotation away from the canonical straight T-pose (`REST_REFERENCE_POSITIONS`),
 * so for a tool-built rig it becomes the bone's local quaternion once the rest alignment of the
 * placement has been applied — the same conversion `BoundRig.applyPose` does, and it must stay
 * the same one: the leak checker deforms vertices from here while the viewport shows the rig.
 *
 * Pure module: three.js math only — no DOM, no editor state.
 */

import { Matrix4 } from 'three';

import { AppError } from '../../shared/errors';
import { CANONICAL_BONE_IDS } from '../../shared/types';
import type { CanonicalBoneId, Pose, QuaternionTuple, SkinData } from '../../shared/types';
import { builtRestJointPositions, type BuiltCanonicalSkeleton } from '../skeletonBuilder';
import {
  IDENTITY_ALIGNMENT,
  absoluteToRelative,
  computeRestAlignment,
  resolveBoneAlignments,
  type BoneAlignment,
} from '../restAlignment';
import type { TestPose, TestPoseId } from './types';

/**
 * Rest alignment of a built skeleton, cached per skeleton. `boneMatricesForPose` runs inside the
 * leak-fix parameter search, so recomputing the swing of 18 bones on every call would be paid
 * hundreds of times for an answer that cannot change: it depends only on the bind pose.
 */
const alignmentCache = new WeakMap<BuiltCanonicalSkeleton, Map<CanonicalBoneId, BoneAlignment>>();

function alignmentFor(built: BuiltCanonicalSkeleton): Map<CanonicalBoneId, BoneAlignment> {
  const cached = alignmentCache.get(built);
  if (cached) return cached;
  const { alignment } = computeRestAlignment(builtRestJointPositions(built));
  const resolved = resolveBoneAlignments(alignment, () => true);
  alignmentCache.set(built, resolved);
  return resolved;
}

const localScratch: QuaternionTuple = [0, 0, 0, 1];

/** Quaternion for a rotation of `degrees` about one canonical axis, as `[x, y, z, w]`. */
function quatAbout(axis: 'x' | 'y' | 'z', degrees: number): QuaternionTuple {
  const half = (degrees * Math.PI) / 360;
  const s = Math.sin(half);
  const c = Math.cos(half);
  if (axis === 'x') return [s, 0, 0, c];
  if (axis === 'y') return [0, s, 0, c];
  return [0, 0, s, c];
}

const ARM_SUBTREE: readonly CanonicalBoneId[] = [
  'LeftUpperArm',
  'LeftLowerArm',
  'LeftHand',
  'RightUpperArm',
  'RightLowerArm',
  'RightHand',
];

const LEG_SUBTREE: readonly CanonicalBoneId[] = [
  'LeftUpperLeg',
  'LeftLowerLeg',
  'LeftFoot',
  'RightUpperLeg',
  'RightLowerLeg',
  'RightFoot',
];

/**
 * The four gallery poses.
 *
 * `movedBones` is always the rotated bones PLUS all of their descendants — the rigid subtree
 * that legitimately travels with the rotation (amendment A3). Anything outside that set which
 * still moves is a leak.
 *
 * Rotation signs (worked out against `DEFAULT_JOINT_POSITIONS`, verified numerically in
 * `tests/rig/autoskin/testPoses.test.ts`):
 * - the LEFT arm points along +X, so a rotation about **+Z** lifts it and about **−Z** drops it;
 *   the right arm (−X) is mirrored;
 * - a leg points along −Y, so a rotation about **−X** swings the thigh forward (+Z) and about
 *   **+X** folds the shin backwards (−Z); both sides use the same sign (sagittal plane);
 * - a **+Y** yaw turns the head toward the character's left (+X).
 *
 * Deep-frozen: consumers read these, they must copy before mutating.
 */
export const TEST_POSES: readonly TestPose[] = deepFreezePoses([
  {
    id: 'armsDown',
    label: 'Arms down',
    pose: {
      LeftUpperArm: quatAbout('z', -80),
      RightUpperArm: quatAbout('z', 80),
    },
    movedBones: [...ARM_SUBTREE],
  },
  {
    id: 'armsUp',
    label: 'Arms up 90°',
    pose: {
      LeftUpperArm: quatAbout('z', 90),
      RightUpperArm: quatAbout('z', -90),
    },
    movedBones: [...ARM_SUBTREE],
  },
  {
    id: 'kneesBent',
    label: 'Knees bent',
    pose: {
      LeftUpperLeg: quatAbout('x', -45),
      RightUpperLeg: quatAbout('x', -45),
      LeftLowerLeg: quatAbout('x', 90),
      RightLowerLeg: quatAbout('x', 90),
    },
    movedBones: [...LEG_SUBTREE],
  },
  {
    id: 'headTurn',
    label: 'Head turn 60°',
    pose: {
      Head: quatAbout('y', 60),
    },
    movedBones: ['Head'],
  },
]);

/** Look one gallery pose up by id. Throws `AppError('UNKNOWN_TEST_POSE')` for anything else. */
export function testPoseById(id: TestPoseId): TestPose {
  for (const pose of TEST_POSES) {
    if (pose.id === id) return pose;
  }
  throw new AppError(
    'UNKNOWN_TEST_POSE',
    `"${String(id)}" is not one of the test poses (${TEST_POSES.map((p) => p.id).join(', ')}).`,
    { id },
  );
}

const matrixScratch = /* @__PURE__ */ new Matrix4();

/**
 * Skinning matrices for `pose`: 16 floats per bone in `CANONICAL_BONE_IDS` order, column-major
 * (three's `Matrix4.elements` layout), each equal to `bone.matrixWorld * skeleton.boneInverses[i]`
 * — exactly what three's own CPU/GPU skinning multiplies a rest vertex by (`bindMatrix` is
 * identity for tool-built rigs, `skeletonBuilder.createSkinnedMesh`).
 *
 * The live skeleton is posed temporarily and **restored to rest** (identity local rotation on
 * every bone plus a fresh `updateMatrixWorld`) before this returns, so callers can treat it as
 * a pure function of `built` and `pose`.
 */
export function boneMatricesForPose(built: BuiltCanonicalSkeleton, pose: Pose): Float32Array {
  const count = CANONICAL_BONE_IDS.length;
  const out = new Float32Array(count * 16);

  // A canonical rotation is measured from the canonical straight T-pose, not from wherever the
  // user placed this skeleton, so it must go through the same rest alignment `BoundRig.applyPose`
  // uses. Skipping it would deform the mesh from one pose while the viewport shows another —
  // silently, and the leak search would then "correct" weights against a pose nobody saw.
  const alignment = alignmentFor(built);

  for (const id of CANONICAL_BONE_IDS) {
    const q = pose[id];
    const bone = built.bones[id];
    if (q) {
      const local = absoluteToRelative(alignment.get(id) ?? IDENTITY_ALIGNMENT, q, localScratch);
      bone.quaternion.set(local[0], local[1], local[2], local[3]);
    } else bone.quaternion.identity();
  }
  built.root.updateMatrixWorld(true);

  const inverses = built.skeleton.boneInverses;
  for (let i = 0; i < count; i += 1) {
    const bone = built.bones[CANONICAL_BONE_IDS[i]];
    const inverse = inverses[i];
    if (inverse) matrixScratch.multiplyMatrices(bone.matrixWorld, inverse);
    else matrixScratch.copy(bone.matrixWorld);
    out.set(matrixScratch.elements, i * 16);
  }

  // Back to rest — a test pose must never leak into the bind pose the solver reads.
  for (const id of CANONICAL_BONE_IDS) built.bones[id].quaternion.identity();
  built.root.updateMatrixWorld(true);

  return out;
}

/**
 * CPU linear-blend skinning in model space: `p' = Σ_slots w · (M[boneIndex] · p)`.
 * Returns a NEW `Float32Array(vertexCount * 3)`; `positions` and `boneMatrices` are untouched.
 *
 * Zero-weight slots are skipped, so their placeholder bone index 0 costs nothing.
 */
export function deformVertices(
  positions: ArrayLike<number>,
  skin: SkinData,
  boneMatrices: Float32Array,
): Float32Array {
  const vertexCount = skin.vertexCount;
  if (positions.length < vertexCount * 3) {
    throw new AppError(
      'SKIN_DATA_MISMATCH',
      `Skin data for mesh "${skin.meshName}" covers ${vertexCount} vertices but only ` +
        `${Math.floor(positions.length / 3)} positions were supplied.`,
      { meshName: skin.meshName, vertexCount, positionCount: positions.length / 3 },
    );
  }
  if (skin.skinIndex.length < vertexCount * 4 || skin.skinWeight.length < vertexCount * 4) {
    throw new AppError(
      'SKIN_DATA_MISMATCH',
      `Skin data for mesh "${skin.meshName}" is not 4 influences per vertex.`,
      {
        meshName: skin.meshName,
        vertexCount,
        skinIndexLength: skin.skinIndex.length,
        skinWeightLength: skin.skinWeight.length,
      },
    );
  }

  const out = new Float32Array(vertexCount * 3);
  const boneCount = Math.floor(boneMatrices.length / 16);

  for (let v = 0; v < vertexCount; v += 1) {
    const p = v * 3;
    const x = positions[p];
    const y = positions[p + 1];
    const z = positions[p + 2];

    let ox = 0;
    let oy = 0;
    let oz = 0;

    for (let slot = 0; slot < 4; slot += 1) {
      const w = skin.skinWeight[v * 4 + slot];
      if (w === 0) continue;
      const bone = skin.skinIndex[v * 4 + slot];
      if (bone < 0 || bone >= boneCount) {
        throw new AppError(
          'SKIN_DATA_MISMATCH',
          `Vertex ${v} of mesh "${skin.meshName}" references bone index ${bone}, but only ` +
            `${boneCount} bone matrices were supplied.`,
          { meshName: skin.meshName, vertex: v, boneIndex: bone, boneCount },
        );
      }
      // column-major: e[col * 4 + row]
      const e = bone * 16;
      ox += w * (boneMatrices[e] * x + boneMatrices[e + 4] * y + boneMatrices[e + 8] * z + boneMatrices[e + 12]);
      oy +=
        w *
        (boneMatrices[e + 1] * x +
          boneMatrices[e + 5] * y +
          boneMatrices[e + 9] * z +
          boneMatrices[e + 13]);
      oz +=
        w *
        (boneMatrices[e + 2] * x +
          boneMatrices[e + 6] * y +
          boneMatrices[e + 10] * z +
          boneMatrices[e + 14]);
    }

    out[p] = ox;
    out[p + 1] = oy;
    out[p + 2] = oz;
  }

  return out;
}

// ---------------------------------------------------------------------------
// internals
// ---------------------------------------------------------------------------

function deepFreezePoses(poses: TestPose[]): readonly TestPose[] {
  for (const pose of poses) {
    Object.freeze(pose.pose);
    for (const q of Object.values(pose.pose)) {
      if (q) Object.freeze(q);
    }
    Object.freeze(pose.movedBones);
    Object.freeze(pose);
  }
  return Object.freeze(poses);
}
